import { createSlice } from '@reduxjs/toolkit';
import { fetchActivityLogsForUserProjectDate } from './thunks/fetchActivityLogsForUserProjectDate.thunk';
import {
  fetchProjectActivityFeed,
  isShownInProjectFeed,
} from './thunks/fetchProjectActivityFeed.thunk';

const emptyDialog = {
  open: false,
  userId: null,
  projectId: null,
  date: null,
};

// The project dashboard's activity feed (newest first) of one project at a
// time; `nextBefore` is the cursor of the next older page, null at the end
// (and it can be set while `items` is empty: the thunk follows the cursor past
// pages with nothing to show only so far, see fetchProjectActivityFeed).
// `incomplete`: the first load covered a stretch back to its `since` (the
// dashboard's day) and stopped at its page limit before reaching it.
const emptyProjectFeed = {
  projectId: null,
  items: [],
  nextBefore: null,
  incomplete: false,
  loading: false,
  loadingMore: false,
  error: null,
};

// The day dialog's rows, and what the viewer may see of the images they are
// about (`images`: file permission id -> { status, name, mimeType,
// rotation }, see the API's resolveActivityLogImages). `requestId` is the
// load whose answer the dialog shows: one for a day the dialog has left (or
// after it closed) is dropped.
const initialState = {
  dialog: emptyDialog,
  items: [],
  images: {},
  loading: false,
  error: null,
  requestId: null,
  projectFeed: emptyProjectFeed,
};

const activityLogSlice = createSlice({
  name: 'activityLog',
  initialState,
  reducers: {
    openActivityLogDialog: (state, action) => {
      const { userId, projectId, date } = action.payload || {};
      state.dialog = { open: true, userId, projectId, date };
      state.items = [];
      state.images = {};
      state.error = null;
    },
    closeActivityLogDialog: (state) => {
      state.dialog = emptyDialog;
      state.items = [];
      state.images = {};
      state.error = null;
      state.loading = false;
      state.requestId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchActivityLogsForUserProjectDate.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.requestId = action.meta.requestId;
      })
      .addCase(
        fetchActivityLogsForUserProjectDate.fulfilled,
        (state, action) => {
          if (state.requestId !== action.meta.requestId) return;
          state.loading = false;
          state.items = action.payload?.items || [];
          state.images = action.payload?.images || {};
        }
      )
      .addCase(
        fetchActivityLogsForUserProjectDate.rejected,
        (state, action) => {
          if (state.requestId !== action.meta.requestId) return;
          state.loading = false;
          state.items = [];
          state.images = {};
          state.error = action.error?.message || 'Failed to load activity logs';
        }
      )
      // A first page starts the feed over for its project (keeping what is
      // shown while the same project reloads); an older page only marks
      // itself loading. Answers for a project the feed has left are dropped.
      .addCase(fetchProjectActivityFeed.pending, (state, action) => {
        const { projectId, before } = action.meta.arg;
        const feed = state.projectFeed;
        if (before) {
          if (feed.projectId === projectId) feed.loadingMore = true;
          return;
        }
        state.projectFeed = {
          ...emptyProjectFeed,
          projectId,
          items: feed.projectId === projectId ? feed.items : [],
          nextBefore: feed.projectId === projectId ? feed.nextBefore : null,
          incomplete: feed.projectId === projectId && feed.incomplete,
          loading: true,
        };
      })
      .addCase(fetchProjectActivityFeed.fulfilled, (state, action) => {
        const { projectId, before } = action.meta.arg;
        const feed = state.projectFeed;
        if (feed.projectId !== projectId) return;
        // The cursor is the API's, whatever of the page is left out here: a
        // page can leave nothing to show while older rows follow.
        const { nextBefore } = action.payload;
        const items = action.payload.items.filter(isShownInProjectFeed);
        if (before) {
          const known = new Set(feed.items.map((item) => item.sk));
          feed.items = feed.items.concat(
            items.filter((item) => !known.has(item.sk))
          );
          feed.loadingMore = false;
        } else {
          feed.items = items;
          feed.loading = false;
          feed.incomplete = Boolean(action.payload.incomplete);
        }
        feed.nextBefore = nextBefore;
        feed.error = null;
      })
      .addCase(fetchProjectActivityFeed.rejected, (state, action) => {
        const { projectId, before } = action.meta.arg;
        const feed = state.projectFeed;
        if (feed.projectId !== projectId) return;
        if (before) {
          feed.loadingMore = false;
        } else {
          feed.loading = false;
        }
        feed.error = action.error?.message || 'Failed to load activity logs';
      });
  },
});

export const { openActivityLogDialog, closeActivityLogDialog } =
  activityLogSlice.actions;

export const selectActivityLogDialog = (state) => state.activityLog.dialog;
export const selectActivityLogItems = (state) => state.activityLog.items;
export const selectActivityLogLoading = (state) => state.activityLog.loading;
export const selectActivityLogError = (state) => state.activityLog.error;
export const selectActivityLogImages = (state) => state.activityLog.images;
export const selectProjectActivityFeed = (state) =>
  state.activityLog.projectFeed;

export default activityLogSlice.reducer;
